import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Chevron, MiniTrajectory, TrajectoryChart } from './Charts.jsx'
import { TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { seriesColor, teamStyle } from './colors.js'
import { useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { STANDINGS_WIDE_PX, useMinWidth } from './layout.js'
import { standingsModel } from './leagueModel.js'
import { teamPath } from './paths.js'
import { READY } from './poll.js'
import { trajectoryModel } from './teamModel.js'
import { backState } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// Standings: both conferences, each row a team, each team's season a small line. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// ONE CONFERENCE AT A TIME ON A PHONE, BOTH FROM 900px, NEVER A SIDEWAYS SCROLL
// ═══════════════════════════════════════════════════════════════════════════
//
// Below 900px the standings are an East and a West segment and one table; from 900px both sit side by side and there is no
// segment at all (a one-option control is a dead control, `GameView.jsx` says the same of its tabs). The component asks
// which it is (`layout.js::useMinWidth`) rather than drawing both and hiding one with CSS, which would put every team
// into the document twice, for a screen reader and for anything that reads the text.
//
// The columns are `# · team · W · L · Pct · GB` and the home and road records (and a neutral one, when the splits leave a
// game over) sit on a second line, so the table never scrolls sideways at 320px. A dashed rule above the first play-in
// team and the first eliminated one marks where the line falls (`clinch` codes `pi` and `o`).
//
// ═══════════════════════════════════════════════════════════════════════════
// EVERY ROW'S LINE IS ON ONE SCALE, AND TAPPING A ROW OPENS ITS CHART IN PLACE
// ═══════════════════════════════════════════════════════════════════════════
//
// The small line is the team's season as games over .500 on the widest scale ANY team reaches (`chartMath.js::
// trajectoryDomain`), so a line in one row compares with a line in another. A row with a trajectory is a button; tapping it
// opens the full chart under it (one open at a time) with a button to the team's page. Nothing is open to begin with:
// the design opens OKC, which is the prototype showing the control off and not a decision about a product's first screen.
// A row with no trajectory is not a button, because there is nothing for it to open.

function Row({ row, season, open, onToggle, teams, picked, onPick }) {
  const team = teams.byId(row.teamId)
  const color = seriesColor(team)
  const chart = open && row.trajectory ? trajectoryModel({ trajectory: row.trajectory, selected: picked ?? null }) : null
  const face = (
    <>
      <span className="d2d-hoops__standrank">{row.rank}</span>
      <TeamMark team={team} tricode={row.abbr} which="away" size="small" />
      <span className="d2d-hoops__standname">{teams.namesOf(row.teamId, row.abbr, row.name).nick || row.abbr}</span>
      <span className="d2d-hoops__standw">{row.w}</span>
      <span className="d2d-hoops__standl">{row.l}</span>
      <span className="d2d-hoops__standpct">{row.pct}</span>
      <span className="d2d-hoops__standgb">{row.gb}</span>
      <span className="d2d-hoops__standsub">
        <span className="d2d-hoops__standrecs d2d-micro" data-testid="hoops-standing-records">
          {row.parts.map((part, index) => (
            <span key={part}>
              {index > 0 ? ' ' : ''}
              <span className="d2d-hoops__rec">{index < row.parts.length - 1 ? `${part} ·` : part}</span>
            </span>
          ))}
        </span>
        <MiniTrajectory mini={row.mini} />
      </span>
    </>
  )
  return (
    <div
      className="d2d-hoops__standrow"
      style={teamStyle(team)}
      data-open={chart ? 'true' : 'false'}
      data-testid="hoops-standing-row"
      data-team-id={row.teamId}
    >
      {row.trajectory ? (
        <button
          type="button"
          className="d2d-hoops__standhead"
          aria-expanded={chart ? 'true' : 'false'}
          data-testid="hoops-standing-toggle"
          onClick={onToggle}
        >
          {face}
        </button>
      ) : (
        <div className="d2d-hoops__standhead d2d-hoops__standhead--static">
          {face}
        </div>
      )}
      {chart === null ? null : (
        <div className="d2d-hoops__standopen" data-testid="hoops-standing-open">
          <TrajectoryChart model={chart} color={color} plotHeight="compact" onSelect={onPick} testId="hoops-standing-traj">
            <Link
              className="d2d-hoops__go"
              to={teamPath(row.teamId, season)}
              state={backState('Standings')}
              data-testid="hoops-standing-team-page"
            >
              Team page
              <Chevron direction="right" />
            </Link>
          </TrajectoryChart>
        </div>
      )}
    </div>
  )
}

function StandingsBody() {
  useDocumentTitle('Standings')
  const choice = useSeasonChoice()
  const teams = useTeams()
  const wide = useMinWidth(STANDINGS_WIDE_PX)
  const [conference, setConference] = useState(null)
  const [openTeam, setOpenTeam] = useState(null)
  const [picked, setPicked] = useState({})
  const season = choice.selected?.season ?? null
  const file = useHistoryResource('standings', season)
  const ready = file.state === READY
  const head = useHead({ season, entry: choice.selected, asOf: file.data?.as_of, source: file.data?.source, ready })

  if (choice.selected === null) return null

  const model = ready ? standingsModel({ standings: file.data, nameOf: (teamId, abbr, full) => teams.nameOf(teamId, abbr, full) }) : null
  const names = model ? model.conferences.map((entry) => entry.name) : []
  const shown = model === null ? [] : wide ? model.conferences : [model.conferences.find((entry) => entry.name === conference) ?? model.conferences[0]].filter(Boolean)
  const leaders = model ? model.conferences.map((entry) => entry.rows[0]).filter(Boolean) : []
  const title =
    model && leaders.length > 0
      ? `${leaders.map((row) => teams.namesOf(row.teamId, row.abbr, row.name).nick).join(' and ')} ${head.finished ? 'finished first' : 'are first'}.`
      : model
        ? 'Standings'
        : null

  return (
    <div className="d2d-hoops__view" data-testid="hoops-standings" data-stale={head.stale ? 'true' : 'false'}>
      <SeasonHead choice={choice} unavailable={NONE} head={head} title={title} back={null} />
      {model === null ? null : (
        <>
          {wide ? null : (
            <div className="d2d-hoops__segments d2d-hoops__segments--fill" role="group" aria-label="Conference">
              {names.map((name) => (
                <button
                  type="button"
                  key={name}
                  className="d2d-hoops__segment"
                  aria-pressed={name === shown[0]?.name ? 'true' : 'false'}
                  data-testid={`hoops-standings-conf-${name}`}
                  onClick={() => setConference(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          <p className="d2d-hoops__panelnote2">
            The small line is each team{'’'}s season in games over .500, on one shared scale. Tap a team for the full chart.
          </p>
          <div className="d2d-hoops__standgrid" data-both={wide ? 'true' : 'false'}>
            {shown.map((entry) => (
              <section className="d2d-hoops__panel d2d-hoops__panel--table" aria-label={entry.name} key={entry.name} data-testid={`hoops-standings-${entry.name}`}>
                <div className="d2d-hoops__standcols d2d-micro" aria-hidden="true">
                  <span>#</span>
                  <span />
                  <span>{entry.name}</span>
                  <span className="d2d-hoops__standright">W</span>
                  <span className="d2d-hoops__standright">L</span>
                  <span className="d2d-hoops__standright">Pct</span>
                  <span className="d2d-hoops__standright">GB</span>
                </div>
                {entry.rows.map((row) => (
                  <div key={row.teamId} className="d2d-hoops__standwrap">
                    {row.cut === null ? null : (
                      <div className="d2d-hoops__cut d2d-micro" data-testid="hoops-standing-cut">
                        {row.cut}
                      </div>
                    )}
                    <Row
                      row={row}
                      season={season}
                      open={openTeam === row.teamId}
                      onToggle={() => setOpenTeam((current) => (current === row.teamId ? null : row.teamId))}
                      teams={teams}
                      picked={picked[`${season}:${row.teamId}`]}
                      onPick={(index) => setPicked((previous) => ({ ...previous, [`${season}:${row.teamId}`]: index }))}
                    />
                  </div>
                ))}
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const NONE = new Set()

export default function StandingsView() {
  return (
    <ViewFrame tab="standings">
      <StandingsBody />
    </ViewFrame>
  )
}
